import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import {
    DemeterDataFrequency,
    DemeterDataSource,
    DemeterMarket,
    DemeterPageElementModel,
    DemeterPageType,
    DemeterRegion,
    DemeterSubRegion,
    ExchangeType,
} from '../../../../Generated/Raven-Demeter';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { TrackingEvent } from '../../../Services/Tracking/TrackingService';
import LinkButton, { LinkButtonType } from '../../Form/Buttons/LinkButton';
import ComponentHeader from '../../Headers/ComponentHeader';
import PageElementBreadcrumb from './PageElementBreadcrumb';
import PageElementContractNumber from './PageElementContractNumber';
import PageElementDataFrequency from './PageElementDataFrequency';
import PageElementExchangeAndCommodity from './PageElementExchangeAndCommodity';
import PageElementMarketAndCommodity from './PageElementMarketAndCommodity';
import styles from './PageElementModal.module.scss';
import { IPageElementModalSelection } from './PageElementModalDefinitions';
import PageElementPageType from './PageElementPageType';
import PageElementRegionAndElement from './PageElementRegionAndElement';
import PageElementSearch from './PageElementSearch';
import PageElementSelected from './PageElementSelected';

export interface IPageElementModalProps {
    title: string;
    open: boolean;
    pageElements: DemeterPageElementModel[];
    selectedPageElement?: DemeterPageElementModel;
    hideChartImage?: boolean;
    handleAdd: (selectedPageElement: DemeterPageElementModel) => void;
    handleClose: () => void;
    resetOnOpen?: boolean;
    allowMultipleSelection?: boolean;
    track: TrackingEvent;
}

const PageElementModal: React.FC<IPageElementModalProps> = (props) => {
    const [translations, translate] = useLanguage();
    const [selectedPageElement, setSelectedPageElement] = useState<DemeterPageElementModel | null>();

    const [selection, setSelection] = useState<IPageElementModalSelection>({});

    const updateSelection = (partial: Partial<IPageElementModalSelection>) => setSelection((prev) => ({ ...prev, ...partial }));

    const isFuturesCategory = selection.pageType === DemeterPageType.Futures;
    const isPricesCategory = selection.pageType === DemeterPageType.PhysicalPrices;
    const showContractNumbers = props.hideChartImage && isFuturesCategory;
    const showDataFrequency = props.hideChartImage && isPricesCategory;

    const equals = (a: any, b: any) => {
        if (a === b || (a == null && b == null)) {
            return true;
        }
        return String(a) === String(b);
    };

    const pageTypePageElements = useMemo(() => {
        if (!selection.pageType) {
            return [];
        }

        return props.pageElements.filter((e) => e.pageType === selection.pageType);
    }, [selection.pageType]);

    const marketAndCommodityPageElements = useMemo(() => {
        const { market, commodityName } = selection;

        if (!commodityName) {
            return [];
        }

        return pageTypePageElements.filter((e) => {
            const commodityDisplayName = e.commodityDisplayName ? translate(e.commodityDisplayName) ?? e.commodityDisplayName : e.commodity ?? '';
            return equals(e.market, market) && equals(commodityDisplayName, commodityName);
        });
    }, [pageTypePageElements, selection]);

    const exchangeAndCommodityPageElements = useMemo(() => {
        const { dataSource, exchange, commodityName, subCommodityName, pageType } = selection;

        if (!commodityName && !subCommodityName) {
            return [];
        }
        if (!dataSource && !exchange) {
            return pageTypePageElements.filter((e) => {
                const commodityDisplayName = e.commodityDisplayName ? translate(e.commodityDisplayName) ?? e.commodityDisplayName : e.commodity ?? '';
                const subCommodityDisplayName = e.subCommodityDisplayName ? translate(e.subCommodityDisplayName) || e.subCommodityDisplayName : e.subCommodity;
                return equals(commodityDisplayName, commodityName) && equals(subCommodityDisplayName, subCommodityName);
            });
        }
        if (pageType === DemeterPageType.Futures) {
            return pageTypePageElements.filter((e) => {
                const commodityDisplayName = e.commodityDisplayName ? translate(e.commodityDisplayName) ?? e.commodityDisplayName : e.commodity ?? '';
                const subCommodityDisplayName = e.subCommodityDisplayName ? translate(e.subCommodityDisplayName) || e.subCommodityDisplayName : e.subCommodity;
                return equals(e.exchange, exchange) && equals(commodityDisplayName, commodityName) && equals(subCommodityDisplayName, subCommodityName);
            });
        }
        return pageTypePageElements.filter((e) => {
            const commodityDisplayName = e.commodityDisplayName ? translate(e.commodityDisplayName) ?? e.commodityDisplayName : e.commodity ?? '';
            const subCommodityDisplayName = e.subCommodityDisplayName ? translate(e.subCommodityDisplayName) || e.subCommodityDisplayName : e.subCommodity;
            return (
                equals(e.dataSource, dataSource) &&
                equals(e.exchange, exchange) &&
                equals(commodityDisplayName, commodityName) &&
                equals(subCommodityDisplayName, subCommodityName)
            );
        });
    }, [pageTypePageElements, selection]);

    const regionPageElements = useMemo(() => {
        const { region, subRegion } = selection;
        if (!region && !subRegion) {
            return [];
        }
        return exchangeAndCommodityPageElements.filter((e) => equals(e.region, region) && equals(e.subRegion, subRegion));
    }, [exchangeAndCommodityPageElements, selection]);

    useEffect(() => {
        if (!props.resetOnOpen) {
            return;
        }

        setSelection({});
    }, [props.open]);

    useEffect(() => {
        if (props.selectedPageElement) {
            const pageElement = props.selectedPageElement;
            const commodityName = pageElement.commodityDisplayName
                ? translate(pageElement.commodityDisplayName) ?? pageElement.commodityDisplayName
                : pageElement.commodity ?? '';
            const subCommodityName = pageElement.subCommodityDisplayName
                ? translate(pageElement.subCommodityDisplayName) || pageElement.subCommodityDisplayName
                : pageElement.subCommodity ?? '';

            setSelectedPageElement(pageElement);
            updateSelection({
                pageType: pageElement.pageType,
                dataSource: pageElement.dataSource,
                exchange: pageElement.exchange,
                commodity: pageElement.commodity,
                subCommodity: pageElement.subCommodity,
                region: pageElement.region,
                subRegion: pageElement.subRegion,
                pageElementType: pageElement.pageElementType,
                commodityName,
                subCommodityName,
                subRegionName: pageElement.subRegionDisplayName,
                displayName: pageElement.displayName,
            });
        }
    }, [props.selectedPageElement]);

    const handlePageTypeSelect = (pageType: DemeterPageType) => {
        setSelectedPageElement(null);
        updateSelection({
            pageType,
            market: null,
            dataSource: null,
            exchange: null,
            commodity: null,
            subCommodity: null,
            region: null,
            subRegion: null,
            pageElementType: null,
            commodityName: null,
            subCommodityName: null,
            subRegionName: null,
            displayName: null,
            contractNumber: undefined,
            dataFrequency: undefined,
        });
    };

    const handleSelectedDataSource = (dataSource: DemeterDataSource | null | undefined) => {
        setSelectedPageElement(null);
        updateSelection({
            dataSource: dataSource ?? null,
            commodity: null,
            subCommodity: null,
            region: null,
            subRegion: null,
            pageElementType: null,
            commodityName: null,
            subCommodityName: null,
            subRegionName: null,
            displayName: null,
        });
    };

    const handleSelectedExchange = (exchange: ExchangeType | null | undefined) => {
        setSelectedPageElement(null);
        updateSelection({
            exchange: exchange ?? null,
            commodity: null,
            subCommodity: null,
            region: null,
            subRegion: null,
            pageElementType: null,
            commodityName: null,
            subCommodityName: null,
            subRegionName: null,
            displayName: null,
        });
    };

    const handleSelectedMarket = (name: string | null | undefined) => {
        setSelectedPageElement(null);
        updateSelection({
            market: (name as DemeterMarket) ?? null,
            region: null,
            subRegion: null,
            pageElementType: null,
            commodityName: null,
            subCommodityName: null,
            subRegionName: null,
            displayName: null,
        });
    };

    const handleSelectedCommodity = (name: string | null | undefined) => {
        setSelectedPageElement(null);
        updateSelection({
            region: null,
            subRegion: null,
            pageElementType: null,
            commodityName: name ?? null,
            subCommodityName: null,
            subRegionName: null,
            displayName: null,
        });
    };

    const handleSelectedSubCommodity = (name: string | null | undefined) => {
        setSelectedPageElement(null);
        updateSelection({
            region: null,
            subRegion: null,
            pageElementType: null,
            subCommodityName: name ?? null,
            subRegionName: null,
            displayName: null,
        });
    };

    const handleSelectedRegion = (region: DemeterRegion | null | undefined) => {
        setSelectedPageElement(null);
        updateSelection({
            region: region ?? null,
            subRegion: null,
            pageElementType: null,
            subRegionName: null,
            displayName: null,
        });
    };

    const handleSelectedSubRegion = (subRegion: DemeterSubRegion | null | undefined, name: string | null | undefined) => {
        setSelectedPageElement(null);
        updateSelection({
            subRegion: subRegion ?? null,
            pageElementType: null,
            subRegionName: name ?? null,
            displayName: null,
        });
    };

    const handleContractSelect = (contractNumber: number) => updateSelection({ contractNumber });

    const handleDataFrequencySelect = (dataFrequency: DemeterDataFrequency) => updateSelection({ dataFrequency });

    const handleSelectedPageElement = (pageElement: DemeterPageElementModel | null | undefined) => {
        setSelectedPageElement(pageElement);
        updateSelection({
            pageElementType: pageElement?.pageElementType,
            displayName: pageElement?.displayName,
        });
    };

    const handleSearchPageElement = (value: IPageElementModalSelection) => {
        setSelectedPageElement(null);
        setSelection(value);
    };

    const handleAdd = () => {
        if (props.hideChartImage) {
            props.handleAdd(selection as DemeterPageElementModel);
            props.handleClose();
        }

        if (!selectedPageElement) {
            return;
        }
        props.handleAdd(selectedPageElement);
        props.handleClose();
    };

    const handleAddAndStay = () => {
        if (props.hideChartImage) {
            props.handleAdd(selection as DemeterPageElementModel);
        }

        if (!selectedPageElement) {
            return;
        }
        props.handleAdd(selectedPageElement);
    };

    const showExchangeAndCommodity =
        selection.pageType !== DemeterPageType.Dashboard &&
        selection.pageType !== DemeterPageType.MonitorsAndIndicators &&
        selection.pageType !== DemeterPageType.NewsAndSummaries &&
        selection.pageType !== DemeterPageType.Presentation &&
        selection.pageType !== DemeterPageType.ValueMatrixCalculator;

    const showMarketAndCommodity = selection.pageType === DemeterPageType.ValueMatrixCalculator;

    const showRegionAndElement =
        selection.pageType !== DemeterPageType.Dashboard &&
        selection.pageType !== DemeterPageType.MonitorsAndIndicators &&
        selection.pageType !== DemeterPageType.NewsAndSummaries &&
        selection.pageType !== DemeterPageType.CommodityIndicators &&
        selection.pageType !== DemeterPageType.HedgeMonitorPro &&
        selection.pageType !== DemeterPageType.Futures &&
        selection.pageType !== DemeterPageType.UserData &&
        selection.pageType !== DemeterPageType.Presentation &&
        selection.pageType !== DemeterPageType.ValueMatrixCalculator;

    const hideImage = selection.pageType === DemeterPageType.Presentation;

    const getPageElements = useMemo(() => {
        if (showMarketAndCommodity) {
            return marketAndCommodityPageElements;
        }
        if (showRegionAndElement) {
            return regionPageElements;
        }
        if (showExchangeAndCommodity) {
            return exchangeAndCommodityPageElements;
        }
        return pageTypePageElements;
    }, [pageTypePageElements, regionPageElements, exchangeAndCommodityPageElements, marketAndCommodityPageElements]);

    useEffect(() => {
        const selectedPageElementIsSet =
            getPageElements.length &&
            equals(getPageElements[0].commodity, selection.commodity) &&
            equals(getPageElements[0].subCommodity, selection.subCommodity) &&
            equals(getPageElements[0].region, selection.region) &&
            equals(getPageElements[0].subRegion, selection.subRegion) &&
            equals(getPageElements[0].exchange, selection.exchange) &&
            equals(getPageElements[0].extraParameters, selection.contractNumber);

        if (!getPageElements.length || selectedPageElementIsSet || !props.hideChartImage) {
            return;
        }

        setSelectedPageElement({
            ...getPageElements[0],
            contractNumber: selection.contractNumber,
            dataFrequency: selection.dataFrequency ?? getPageElements[0].dataFrequency,
        } as DemeterPageElementModel);
    }, [getPageElements]);

    return (
        <Dialog open={props.open} onClose={props.handleClose} sx={{ '& .MuiDialog-paper': { maxWidth: '1200px' } }}>
            <DialogTitle>
                <div className={styles.page_element_modal_header}>
                    <ComponentHeader title={props.title} />
                    <div className={styles.page_element_modal_search}>
                        <PageElementSearch
                            pageElements={props.pageElements}
                            handleSelect={handleSearchPageElement}
                            showExchangeAndCommodity
                            showRegionAndElement
                            showContractNumbers
                            showDataFrequency
                            track={{ eventCategory: props.track.eventCategory, eventAction: 'ModalPageElementSearchChanged' }}
                        />
                    </div>
                    <CloseIcon className={styles.page_element_modal_close} onClick={props.handleClose} />
                </div>
            </DialogTitle>
            <DialogContent>
                <PageElementBreadcrumb selected={selection} />
                <div className={styles.page_element_modal_content}>
                    <div className={styles.page_element_modal_panes}>
                        <div className={styles.page_element_modal_pane}>
                            <PageElementPageType
                                selection={selection}
                                pageElements={props.pageElements}
                                handlePageTypeSelect={handlePageTypeSelect}
                                track={{
                                    eventCategory: props.track.eventCategory,
                                    eventAction: `${props.track.eventAction}TypeSelected`,
                                }}
                            />
                        </div>
                        {showMarketAndCommodity && (
                            <div className={styles.page_element_modal_pane}>
                                <PageElementMarketAndCommodity
                                    selection={selection}
                                    pageElements={pageTypePageElements}
                                    handleMarketSelect={handleSelectedMarket}
                                    handleCommoditySelect={handleSelectedCommodity}
                                    track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}MarketAndCommoditySelected` }}
                                />
                            </div>
                        )}
                        {showExchangeAndCommodity && (
                            <div className={styles.page_element_modal_pane}>
                                <PageElementExchangeAndCommodity
                                    selection={selection}
                                    pageElements={pageTypePageElements}
                                    handleDataSourceSelect={handleSelectedDataSource}
                                    handleExchangeSelect={handleSelectedExchange}
                                    handleCommoditySelect={handleSelectedCommodity}
                                    handleSubCommoditySelect={handleSelectedSubCommodity}
                                    track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}ExchangeOrCommoditySelected` }}
                                />
                            </div>
                        )}
                        {showRegionAndElement && (
                            <div className={styles.page_element_modal_pane}>
                                <PageElementRegionAndElement
                                    selection={selection}
                                    selectedPageElement={selectedPageElement}
                                    pageElements={exchangeAndCommodityPageElements}
                                    handleRegionSelect={handleSelectedRegion}
                                    handleSubRegionSelect={handleSelectedSubRegion}
                                    track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}RegionOrElementSelected` }}
                                />
                            </div>
                        )}
                        {showContractNumbers && (
                            <div className={styles.page_element_modal_pane}>
                                <PageElementContractNumber
                                    selection={selection}
                                    contracts={selectedPageElement === null ? [] : [1, 2, 3]}
                                    handleContractSelect={handleContractSelect}
                                    track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}ContractNumbersSelected` }}
                                />
                            </div>
                        )}

                        {showDataFrequency && (
                            <div className={styles.page_element_modal_pane}>
                                <PageElementDataFrequency
                                    availableFrequencies={selectedPageElement?.extraParameters ?? ''}
                                    selection={selection}
                                    handleDataFrequencySelect={handleDataFrequencySelect}
                                    track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}DataFrequencySelected` }}
                                />
                            </div>
                        )}
                        {!props.hideChartImage && (
                            <div className={styles.page_element_modal_pane_long}>
                                <PageElementSelected
                                    pageElements={getPageElements}
                                    selectedPageElement={selectedPageElement}
                                    hideImage={hideImage}
                                    handlePageElementSelect={handleSelectedPageElement}
                                    track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}ElementSelected` }}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
            <DialogActions>
                <div className={styles.page_element_modal_actions}>
                    <LinkButton
                        title={translations.actions.cancel}
                        type={LinkButtonType.White}
                        onClick={props.handleClose}
                        track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}CancelClicked` }}
                    />
                    {props.allowMultipleSelection && (
                        <LinkButton
                            title={translations.actions.addAndStay}
                            type={LinkButtonType.Blue}
                            onClick={handleAddAndStay}
                            disabled={(!selectedPageElement && !props.hideChartImage) || (props.hideChartImage && !selection.region && !selection.exchange)}
                            track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}AddAndStayClicked` }}
                        />
                    )}
                    <LinkButton
                        title={translations.actions.add}
                        type={LinkButtonType.Blue}
                        onClick={handleAdd}
                        disabled={(!selectedPageElement && !props.hideChartImage) || (props.hideChartImage && !selection.region && !selection.exchange)}
                        track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}AddClicked` }}
                    />
                </div>
            </DialogActions>
        </Dialog>
    );
};

export default PageElementModal;
